import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

const initialActionsState = {
  CREATE: false,
  UPDATE: false,
  READ: false,
  DELETE: false,
};
// --------------dummy data----------------
// const permissionsData = {
//   stock_product: [""],
//   service_listing: ["all"],
//   offers: ["all"],
//   sales_order: ["all"],
//   invoices: ["all"],
//   quotations:["all"],
//   customers: ["all"],
//   credit_wallet: ["read"],
//   services: ["all"],
//   service_request: ["all"],
//   job_card: ["all"],
//   po_form_customer: ["all"],
//   purchase_order: ["all"],
//   bills: ["all"],
//   vendors: ["all"],
//   inventory_reports: ["all"],
//   sales_reports: ["all"],
//   target_reports: ["all"],
//   "p&l_reports": ["all"],
//   tax_reports: ["all"],
//   payments: ["all"],
//   users: ["all"],
//   business: ["all"],
//   business_settings: ["all"],
//   expenses: ["all"],
// };

export const usePermissions = (feature) => {
  const [actions, setActions] = useState(initialActionsState);
  const { details } = useSelector((state) => state.user);
  useEffect(() => {
    const filteredActions = { ...initialActionsState };

    if (details?.roleAndPermissions?.permissions && feature) {
      const permissions = details.roleAndPermissions.permissions[feature];
      // const permissions = permissionsData[feature];

      if (permissions?.includes("all")) {
        // Enable all actions
        setActions({
          CREATE: true,
          UPDATE: true,
          READ: true,
          DELETE: true,
        });
      } else if (permissions?.length > 0) {
        // Set specific actions based on permissions
        setActions({
          CREATE: permissions.includes("create"),
          UPDATE: permissions.includes("update"),
          READ: permissions.includes("read"),
          DELETE: permissions.includes("delete"),
        });
      } else {
        // No permissions, disable all actions
        setActions(initialActionsState);
      }
    } else {
      setActions(initialActionsState); // Default state if no permissions are found
    }
  }, [details, feature]);

  return actions;
};

//------------------------Permission Transform Hook----------------

export const transformPermissions = (userPermissions) => {
  const transformPermissions = (permissions) => {
    const categoryMapping = {
      product_management: ["stock_product", "service_listing", "offers"],
      sales: [
        "sales_order",
        "invoices",
        "quotations",
        "customers",
        "credit_wallet",
        "sales_target",
      ],
      services: ["services", "service_request", "job_card", "tools"],
      purchases: ["po_form_customer", "purchase_order", "bills", "vendors"],
      reports: [
        "inventory_reports",
        "sales_reports",
        "p&l_reports",
        "tax_reports",
        "target_reports",
        "ladger_reports",
        "sales_book",
        "purchase_book"
      ],
      receivables: ["payments"],
      settings: ["users", "employees", "employees_expenses", "business", "business_settings", "expenses"],
    };

    const categoryLinks = {
      product_management: "/product-management",
      sales: "/sales",
      services: "/services",
      purchases: "/purchase",
      reports: "/reports",
      receivables: "/receivables",
      settings: "/admin",
    };

    const featureLinks = {
      stock_product: "/product-management/Stocked-Products",
      service_listing: "/product-management/service-listing",
      offers: "/product-management/offer",
      sales_order: "/sales/order",
      invoices: "/sales/invoices",
      quotations: "/sales/quotations",
      customers: "/sales/customers",
      sales_target: "/sales/sales-target",
      credit_wallet: "/sales/wallet",
      services: "/services/service-list",
      service_request: "/services/service-request",
      job_card: "/services/job-card",
      tools: "/services/tools-list",
      po_form_customer: "/purchase/from-customer",
      purchase_order: "/purchase/order",
      bills: "/purchase/bills",
      vendors: "/purchase/vendors",
      inventory_reports: "/reports/inventory-report",
      sales_reports: "/reports/sales-report",
      "p&l_reports": "/reports/profit-loss-report",
      tax_reports: "/reports/tax-report",
      target_reports: "/reports/sales-target-report",
      ladger_reports: "/reports/ladger-report",
      sales_book: "/reports/sales-book-report",
      purchase_book: "/reports/purchase-report",
      payments: "/receivables/payments",
      users: "/admin/users",
      business: "/admin/business",
      business_settings: "/admin/business-settings",
      expenses: "/admin/expenses",
      employees: "/admin/employees",
      employees_expenses: "/admin/employees-expenses",
    };

    const mapActions = (featurePermissions) => {
      if (featurePermissions.includes("all")) {
        return { CREATE: true, UPDATE: true, DELETE: true, READ: true };
      }

      if (featurePermissions.length === 0) {
        return { CREATE: false, UPDATE: false, DELETE: false, READ: false };
      }

      return {
        CREATE: featurePermissions.includes("create"),
        UPDATE: featurePermissions.includes("update"),
        DELETE: featurePermissions.includes("delete"),
        READ: featurePermissions.includes("read"),
      };
    };

    const transformed = {};

    Object.entries(categoryMapping).forEach(([category, features]) => {
      transformed[category] = { linkUrl: categoryLinks[category] };

      features.forEach((feature) => {
        const featurePermissions = permissions[feature] || [];
        transformed[category][feature] = {
          linkUrl: featureLinks[feature],
          ...mapActions(featurePermissions),
        };
      });
    });
    return transformed;
  };

  return userPermissions
    ? transformPermissions(userPermissions)
    : // ?  transformPermissions(permissionsData)
      {};
};

// -------------------------------------------------------------------------------

export function filterRoutes(transformedPermissions, routes) {
  function hasValidPermissions(permissionObj) {
    // Check if at least one permission (CREATE, UPDATE, DELETE, READ) is true
    return Object.values(permissionObj).some(Boolean);
  }

  function filterRouteNodes(permissions, routeNodes) {
    return routeNodes
      .map((routeNode) => {
        // Get the path segments to determine the corresponding permission
        const pathSegments =
          routeNode.props.path?.split("/").filter(Boolean) || [];
        let currentPermissions = permissions;

        // Traverse the permission object using path segments
        for (const segment of pathSegments) {
          currentPermissions = currentPermissions?.[segment.replace(/-/g, "_")];
          if (!currentPermissions) break;
        }

        // If no valid permissions for this route, skip it
        if (
          !currentPermissions ||
          (typeof currentPermissions === "object" &&
            !hasValidPermissions(currentPermissions))
        ) {
          return null;
        }

        // If the route has children, filter them recursively
        const children = routeNode.props.children;
        let filteredChildren = null;
        if (children) {
          filteredChildren = filterRouteNodes(
            permissions,
            React.Children.toArray(children)
          );
          // Remove the route if all child routes are filtered out
          if (filteredChildren.length === 0) {
            return null;
          }
        }

        // Return the route node with filtered children
        return React.cloneElement(routeNode, { children: filteredChildren });
      })
      .filter(Boolean); // Remove null values
  }

  // Start filtering from the top-level routes

  return filterRouteNodes(
    transformedPermissions,
    React.Children.toArray(routes.props.children)
  );
}

// ------------------------------------------------------------------------------

export function filterNavbarRoutesByPermissions(permissions, adminLinks) {
  const transformedPermissions = transformPermissions(permissions);
  return adminLinks
    .map((link) => {
      const moduleKey = Object.keys(transformedPermissions).find(
        (key) => transformedPermissions[key].linkUrl === link.url
      );

      if (link.url === "/") return link;
      if (!moduleKey) return null; // Skip if module key doesn't exist in permissions
      if (link.children) {
        // Filter children based on READ permissions
        const filteredChildren = link.children.filter((child) => {
          const childKey = Object.keys(transformedPermissions[moduleKey]).find(
            (key) =>
              transformedPermissions[moduleKey][key]?.linkUrl === child.url
          );

          return childKey && transformedPermissions[moduleKey][childKey]?.READ;
        });
        // If no children have READ permissions, skip the parent
        if (filteredChildren.length === 0) return null;

        // Include the parent with the filtered children
        return { ...link, children: filteredChildren };
      } else {
        // For routes without children, check READ permission
        return transformedPermissions[moduleKey]?.READ ? link : null;
      }
    })
    .filter(Boolean); // Remove null values
}
